/**
 * Doctor Signatures — Settings > Documents & Templates > Doctor Signatures.
 *
 * Two modes:
 *   1. The logged-in doctor manages their own signature (upload / draw / clear).
 *   2. Admins can also upload signatures on behalf of another doctor in the
 *      same clinic (via /doctor/signature/{doctor_id}).
 *
 * Every signature is stored per-user and served through an auth-gated URL
 * (`/api/public/doctor-signature/{doctor_id}`). Historical documents freeze
 * the signature at generation time — changing the profile signature never
 * retroactively re-signs old prescriptions.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Upload, Signature as SignatureIcon, Trash2, Loader2,
  Pencil, Check, X, RotateCcw, ImageIcon,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { apiError } from "../../lib/apiError";
import { useAuth } from "../../lib/auth";

const backendBase = () => (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function DoctorSignatures() {
  const { user } = useAuth();
  const [sig, setSig]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawOpen, setDrawOpen] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/doctor/signature");
      setSig(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const uploadFile = async (f) => {
    if (!f) return;
    const okExt = /\.(png|jpg|jpeg)$/i.test(f.name);
    if (!okExt) { toast.error("PNG or JPG only."); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("Max 2 MB"); return; }
    const fd = new FormData(); fd.append("file", f);
    try {
      await api.post("/doctor/signature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Signature saved");
      load();
    } catch (e) { toast.error(apiError(e, "Upload failed")); }
  };

  const clearSig = async () => {
    if (!window.confirm("Clear your saved signature? Historical prescriptions keep their frozen signature.")) return;
    try {
      await api.delete("/doctor/signature");
      toast.success("Signature cleared");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const currentUrl = sig?.has_signature ? `${backendBase()}${sig.signature_url}?t=${sig.signature_updated_at}` : null;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="doctor-signatures-page">
      <div>
        <Link to="/app/settings" className="inline-flex items-center gap-1.5 text-xs text-ink-mid hover:text-ink mb-2">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Settings
        </Link>
        <h1 className="text-2xl font-medium text-ink" data-testid="doctor-signatures-title">Doctor Signatures</h1>
        <p className="mt-1 text-sm text-ink-mid max-w-2xl">
          Your saved signature auto-embeds in every prescription you generate. Historical documents freeze the signature they were created with — changing this never touches old records.
        </p>
      </div>

      {/* My signature */}
      <section className="rounded-3xl bg-white border border-[#E7E1D3] p-6 space-y-4" data-testid="my-signature-section">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-mid">My signature</div>
            <div className="text-lg font-medium text-ink mt-0.5">{user?.name || user?.email}</div>
          </div>
          {sig?.has_signature && (
            <button onClick={clearSig}
                     className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 px-3 py-1.5 rounded-full"
                     data-testid="clear-signature-btn">
              <Trash2 className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center text-sm text-ink-mid py-6">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : sig?.has_signature ? (
          <div className="rounded-2xl bg-linen border border-[#E7E1D3] p-6 flex items-center justify-center h-40"
               data-testid="current-signature-preview">
            <img src={currentUrl} alt="Doctor signature" className="max-h-full max-w-full object-contain" />
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-[#E7E1D3] p-8 text-center text-sm text-ink-mid"
               data-testid="no-signature-state">
            <SignatureIcon className="w-8 h-8 mx-auto mb-2" />
            No signature saved yet. Upload a scan or draw one now.
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#E7E1D3]">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                  onChange={(e) => uploadFile(e.target.files?.[0])} className="hidden"
                  data-testid="signature-file-input" />
          <button onClick={() => fileRef.current?.click()}
                   className="inline-flex items-center gap-2 text-sm font-medium text-white bg-ink hover:bg-black px-5 py-2.5 rounded-full transition-colors"
                   data-testid="upload-signature-btn">
            <Upload className="w-4 h-4" /> {sig?.has_signature ? "Replace" : "Upload"}
          </button>
          <button onClick={() => setDrawOpen(true)}
                   className="inline-flex items-center gap-2 text-sm font-medium text-ink bg-linen hover:bg-[#E7E1D3] px-5 py-2.5 rounded-full transition-colors"
                   data-testid="draw-signature-btn">
            <Pencil className="w-4 h-4" /> Draw
          </button>
          <span className="text-xs text-ink-mid ml-2">PNG (transparent preferred) or JPG · max 2 MB</span>
        </div>
      </section>

      {/* Info card */}
      <section className="rounded-3xl bg-linen border border-[#E7E1D3] p-5">
        <div className="text-xs uppercase tracking-wider text-ink-mid">How this works</div>
        <ul className="mt-2 space-y-1.5 text-sm text-ink-mid list-disc pl-5">
          <li>Your signature is auto-inserted wherever a template has a Signature field mapped to "Logged-in doctor".</li>
          <li>Signatures are private — only members of your clinic can fetch them.</li>
          <li>Changing your signature never re-signs historical prescriptions — those keep the signature they were finalized with.</li>
        </ul>
      </section>

      {drawOpen && (
        <DrawSignatureDialog onClose={() => setDrawOpen(false)} onDone={() => { setDrawOpen(false); load(); }} />
      )}
    </div>
  );
}

/* -------------------------------------------------------- draw dialog */
function DrawSignatureDialog({ onClose, onDone }) {
  const canvasRef = useRef();
  const [drawing, setDrawing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#111318"; ctx.lineWidth = 2.5; ctx.lineCap = "round";
  }, []);

  const start = (e) => {
    setDrawing(true); setEmpty(false);
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing) return;
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(p.x, p.y); ctx.stroke();
  };
  const end = () => setDrawing(false);
  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cx - r.left) * (c.width / r.width), y: (cy - r.top) * (c.height / r.height) };
  };

  const clear = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height);
    setEmpty(true);
  };

  const save = async () => {
    if (empty) { toast.error("Please sign in the box first."); return; }
    setBusy(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await api.post("/doctor/signature/draw", { data_url: dataUrl });
      toast.success("Signature saved");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()}
           data-testid="draw-signature-dialog">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-ink">Draw signature</h2>
          <button onClick={onClose} className="text-ink-mid hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        <canvas
          ref={canvasRef} width={600} height={200}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={(e) => { e.preventDefault(); start(e); }}
          onTouchMove={(e) => { e.preventDefault(); move(e); }}
          onTouchEnd={end}
          className="w-full h-52 rounded-2xl border-2 border-dashed border-[#E7E1D3] bg-white cursor-crosshair touch-none"
          data-testid="signature-canvas"
        />
        <div className="flex items-center justify-between">
          <button onClick={clear}
                   className="inline-flex items-center gap-1.5 text-xs text-ink-mid hover:text-ink px-3 py-1.5 rounded-full"
                   data-testid="signature-clear-btn">
            <RotateCcw className="w-3.5 h-3.5" /> Clear
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-sm text-ink-mid hover:text-ink px-4 py-2">Cancel</button>
            <button onClick={save} disabled={busy || empty}
                     className="inline-flex items-center gap-2 text-sm font-medium text-white bg-ink hover:bg-black px-5 py-2.5 rounded-full transition-colors disabled:opacity-50"
                     data-testid="signature-save-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save signature
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
